/**
 * BOSS Anlık Maliyet sayfası — TAM CANLI versiyonu, ikili büyük kart.
 *
 * v2: Mesai dışındaki personel kırmızı renkli kartta gösterilir.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import {
  Coins, Users, Activity, ChevronLeft, ChevronRight, Clock,
  TrendingUp, Calculator, MoonStar, Filter, X,
} from 'lucide-react'
import {
  tumAktifPersonellerinMaliyeti,
  aktifMesaiMaliyeti,
  type ToplamMaliyetOzeti,
  type AktifMaliyetOzeti,
} from '@/lib/maas/toplam-maliyet'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import type { Sube, Pozisyon } from '@/types'

function buAyStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function ayEtiketi(ay: string): string {
  const [y, m] = ay.split('-').map(Number)
  if (!y || !m) return ay
  const aylar = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
  ]
  return `${aylar[m - 1]} ${y}`
}

function paraTam(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function paraKurus(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function MaliyetPage() {
  const [donem, setDonem] = useState<string>(buAyStr())
  const [ozet, setOzet] = useState<ToplamMaliyetOzeti | null>(null)
  const [aktifMaliyet, setAktifMaliyet] = useState<AktifMaliyetOzeti | null>(null)
  const [subeler, setSubeler] = useState<Map<string, Sube>>(new Map())
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)

  // Filtreler
  const [fSube, setFSube] = useState<string>('hepsi')
  const [fPozisyon, setFPozisyon] = useState<string>('hepsi')
  const [fDurum, setFDurum] = useState<'hepsi' | 'mesaide' | 'disi'>('hepsi')
  const [fMaasTipi, setFMaasTipi] = useState<'hepsi' | 'aylik' | 'saatlik'>('hepsi')

  const baseToplamAnlikRef = useRef<number>(0)
  const basePersonelAnlikRef = useRef<Map<string, number>>(new Map())
  const baseTimestampRef = useRef<number>(Date.now())
  const [yansitilanToplam, setYansitilanToplam] = useState<number>(0)
  const [yansitilanSatirlar, setYansitilanSatirlar] = useState<Map<string, number>>(new Map())

  const isBuAy = donem === buAyStr()

  const tumYukle = useCallback(async () => {
    try {
      setYukleniyor(true)
      const [o, m, s, p] = await Promise.all([
        tumAktifPersonellerinMaliyeti(donem),
        aktifMesaiMaliyeti(),
        tumSubeleriGetir(),
        tumPozisyonlariGetir(),
      ])
      setOzet(o)
      setAktifMaliyet(m)
      const sm = new Map<string, Sube>()
      s.forEach((x) => sm.set(x.id, x))
      setSubeler(sm)
      setPozisyonlar(p.filter((x) => x.aktif !== false))

      baseToplamAnlikRef.current = o.toplamAnlikMaliyet
      const personelAnlik = new Map<string, number>()
      o.satirlar.forEach((s) => personelAnlik.set(s.personel.id, s.anlikMaliyet))
      basePersonelAnlikRef.current = personelAnlik
      baseTimestampRef.current = Date.now()
      setYansitilanToplam(o.toplamAnlikMaliyet)
      setYansitilanSatirlar(new Map(personelAnlik))

      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Hesaplanamadı: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }, [donem])

  useEffect(() => {
    tumYukle()
  }, [tumYukle])

  useEffect(() => {
    if (!isBuAy) return
    const interval = setInterval(async () => {
      try {
        const [o, m] = await Promise.all([
          tumAktifPersonellerinMaliyeti(donem),
          aktifMesaiMaliyeti(),
        ])
        setOzet(o)
        setAktifMaliyet(m)

        baseToplamAnlikRef.current = o.toplamAnlikMaliyet
        const personelAnlik = new Map<string, number>()
        o.satirlar.forEach((s) => personelAnlik.set(s.personel.id, s.anlikMaliyet))
        basePersonelAnlikRef.current = personelAnlik
        baseTimestampRef.current = Date.now()
      } catch (err) {
        console.error(err)
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [donem, isBuAy])

  useEffect(() => {
    if (!isBuAy || !aktifMaliyet || !ozet) return

    if (aktifMaliyet.saatlikToplam === 0) {
      setYansitilanToplam(baseToplamAnlikRef.current)
      setYansitilanSatirlar(new Map(basePersonelAnlikRef.current))
      return
    }

    const interval = setInterval(() => {
      const gecenSn = (Date.now() - baseTimestampRef.current) / 1000
      const toplamBirikim = (aktifMaliyet.saatlikToplam / 3600) * gecenSn
      setYansitilanToplam(baseToplamAnlikRef.current + toplamBirikim)

      const yeniSatirlar = new Map(basePersonelAnlikRef.current)
      aktifMaliyet.detay.forEach(({ personelId, saatlik }) => {
        const baseAnlik = yeniSatirlar.get(personelId) ?? 0
        const birikim = (saatlik / 3600) * gecenSn
        yeniSatirlar.set(personelId, baseAnlik + birikim)
      })
      setYansitilanSatirlar(yeniSatirlar)
    }, 200)

    return () => clearInterval(interval)
  }, [aktifMaliyet, ozet, isBuAy])

  function ayDegistir(yon: -1 | 1) {
    const [y, m] = donem.split('-').map(Number)
    if (!y || !m) return
    const d = new Date(y, m - 1 + yon, 1)
    setDonem(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  const canliAkiyor = !!(isBuAy && aktifMaliyet && aktifMaliyet.saatlikToplam > 0)

  // ── Filtreleme ──
  function satirAktifMi(personelId: string): boolean {
    return aktifMaliyet?.detay.some((d) => d.personelId === personelId) ?? false
  }

  const tumSatirlar = ozet?.satirlar ?? []
  const filtreliSatirlar = tumSatirlar.filter(({ personel }) => {
    if (fSube !== 'hepsi' && !(personel.subeIdleri ?? []).includes(fSube)) return false
    if (fPozisyon !== 'hepsi' && !(personel.pozisyonIdleri ?? []).includes(fPozisyon)) return false
    if (fMaasTipi !== 'hepsi' && personel.maasTipi !== fMaasTipi) return false
    if (fDurum === 'mesaide' && !satirAktifMi(personel.id)) return false
    if (fDurum === 'disi' && satirAktifMi(personel.id)) return false
    return true
  })

  const filtreVar = fSube !== 'hepsi' || fPozisyon !== 'hepsi'
    || fDurum !== 'hepsi' || fMaasTipi !== 'hepsi'

  // Filtreli toplamlar (canlı yansıma dahil)
  const filtreliAnlikToplam = filtreliSatirlar.reduce((acc, s) => {
    const yan = yansitilanSatirlar.get(s.personel.id) ?? s.anlikMaliyet
    return acc + yan
  }, 0)
  const filtreliNetToplam = filtreliSatirlar.reduce((acc, s) => acc + s.tahminiNet, 0)

  // Filtreli canlı saatlik maliyet (turuncu kart için)
  const filtreliIdSet = new Set(filtreliSatirlar.map((s) => s.personel.id))
  const filtreliAktifDetay = (aktifMaliyet?.detay ?? []).filter((d) => filtreliIdSet.has(d.personelId))
  const filtreliSaatlikToplam = filtreliAktifDetay.reduce((acc, d) => acc + d.saatlik, 0)
  const filtreliMesaideSayisi = filtreliAktifDetay.length

  // Ay sonu tahmini bazlı saniyelik maliyet (toplam ÷ ayın saniyesi, 7/24 ortalama)
  const [aYil, aAy] = donem.split('-').map(Number)
  const ayGunSayisi = new Date(aYil!, aAy!, 0).getDate()
  const aySonuToplam = filtreVar ? filtreliNetToplam : (ozet?.toplamTahminiNet ?? 0)
  const aySonuSaniyelik = aySonuToplam / (ayGunSayisi * 86400)
  const aySonuSaatlik = aySonuSaniyelik * 3600
  const aySonuGunluk = aySonuSaniyelik * 86400

  function filtreleriTemizle() {
    setFSube('hepsi'); setFPozisyon('hepsi'); setFDurum('hepsi'); setFMaasTipi('hepsi')
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Coins className="w-6 h-6 text-amber-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Maliyet</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Personel anlık biriken maliyet — canlı
          </p>
        </div>
      </div>

      {aktifMaliyet && isBuAy && (
        <div className="card bg-gradient-to-br from-amber-500 to-orange-600 border-0 text-white mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 animate-pulse" />
            <h2 className="font-bold text-sm uppercase tracking-wide opacity-90">
              {filtreVar ? 'Filtreli — Şu Anki Saatlik Maliyet' : 'Şu Anki Saatlik Maliyet'}
            </h2>
          </div>
          <p className="text-4xl font-bold tabular-nums">
            {paraTam(filtreVar ? filtreliSaatlikToplam : aktifMaliyet.saatlikToplam)}
            <span className="text-2xl ml-1 opacity-80">₺/saat</span>
          </p>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div className="bg-white/15 rounded-lg px-2 py-1.5">
              <div className="opacity-75">Mesaide</div>
              <div className="font-semibold flex items-center gap-1">
                <Users className="w-3 h-3" />
                {filtreVar ? filtreliMesaideSayisi : aktifMaliyet.aktifPersonelSayisi} kişi
              </div>
            </div>
            <div className="bg-white/15 rounded-lg px-2 py-1.5">
              <div className="opacity-75">Saniyede</div>
              <div className="font-semibold tabular-nums">
                {paraKurus((filtreVar ? filtreliSaatlikToplam : aktifMaliyet.saatlikToplam) / 3600)} ₺
              </div>
            </div>
          </div>
        </div>
      )}

      {ozet && (
        <div className="card bg-gradient-to-br from-indigo-500 to-violet-600 border-0 text-white mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="w-5 h-5" />
            <h2 className="font-bold text-sm uppercase tracking-wide opacity-90">
              {filtreVar ? 'Filtreli — Ay Sonu Bazlı Saniyelik' : 'Ay Sonu Bazlı Saniyelik Maliyet'}
            </h2>
          </div>
          <p className="text-4xl font-bold tabular-nums">
            {paraKurus(aySonuSaniyelik)}
            <span className="text-2xl ml-1 opacity-80">₺/saniye</span>
          </p>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div className="bg-white/15 rounded-lg px-2 py-1.5">
              <div className="opacity-75">Saatte</div>
              <div className="font-semibold tabular-nums">{paraTam(aySonuSaatlik)} ₺</div>
            </div>
            <div className="bg-white/15 rounded-lg px-2 py-1.5">
              <div className="opacity-75">Günde</div>
              <div className="font-semibold tabular-nums">{paraTam(aySonuGunluk)} ₺</div>
            </div>
          </div>
          <p className="text-[10px] opacity-70 mt-2">
            Ay sonu tahmini toplam ÷ ayın günü ({ayGunSayisi} gün, 7/24 ortalama)
          </p>
        </div>
      )}

      <div className="card mb-4">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => ayDegistir(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center font-semibold text-gray-900">
            {ayEtiketi(donem)}
          </div>
          <button onClick={() => ayDegistir(1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => setDonem(buAyStr())}
            className="px-3 py-1.5 text-xs text-brand-600 hover:underline whitespace-nowrap"
          >
            Bu ay
          </button>
        </div>

        {ozet && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div className="bg-blue-50 rounded-lg p-2">
              <div className="text-xs text-blue-700">Personel</div>
              <div className="font-bold text-blue-900">{ozet.personelSayisi}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-2">
              <div className="text-xs text-purple-700">Toplam Saat</div>
              <div className="font-bold text-purple-900">
                {Math.round(ozet.toplamCalismaSaat)}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <div className="text-xs text-green-700">Puantaj</div>
              <div className={`font-bold ${ozet.toplamPuantaj >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                {ozet.toplamPuantaj >= 0 ? '+' : ''}{paraTam(ozet.toplamPuantaj)} ₺
              </div>
            </div>
            <div className="bg-amber-50 rounded-lg p-2">
              <div className="text-xs text-amber-700">Avans</div>
              <div className="font-bold text-amber-900">
                {paraTam(ozet.toplamOdenmisAvans)} ₺
              </div>
            </div>
          </div>
        )}
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm mb-4">
          {hata}
        </div>
      )}

      {!yukleniyor && ozet && ozet.satirlar.length > 0 && (
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <h2 className="font-bold text-sm text-gray-900">Filtrele</h2>
            {filtreVar && (
              <button
                onClick={filtreleriTemizle}
                className="ml-auto text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Temizle
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={fSube}
              onChange={(e) => setFSube(e.target.value)}
              className="input-base text-sm"
            >
              <option value="hepsi">Tüm şubeler</option>
              {Array.from(subeler.values()).map((s) => (
                <option key={s.id} value={s.id}>{s.isim}</option>
              ))}
            </select>
            <select
              value={fPozisyon}
              onChange={(e) => setFPozisyon(e.target.value)}
              className="input-base text-sm"
            >
              <option value="hepsi">Tüm pozisyonlar</option>
              {pozisyonlar.map((p) => (
                <option key={p.id} value={p.id}>{p.isim}</option>
              ))}
            </select>
            <select
              value={fDurum}
              onChange={(e) => setFDurum(e.target.value as typeof fDurum)}
              className="input-base text-sm"
            >
              <option value="hepsi">Tüm durumlar</option>
              <option value="mesaide">Sadece mesaide</option>
              <option value="disi">Sadece mesai dışı</option>
            </select>
            <select
              value={fMaasTipi}
              onChange={(e) => setFMaasTipi(e.target.value as typeof fMaasTipi)}
              className="input-base text-sm"
            >
              <option value="hepsi">Tüm maaş tipleri</option>
              <option value="aylik">Aylık</option>
              <option value="saatlik">Saatlik</option>
            </select>
          </div>
        </div>
      )}

      {yukleniyor ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          <p className="text-sm text-gray-600 mt-3">Hesaplanıyor...</p>
        </div>
      ) : !ozet || ozet.satirlar.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="font-semibold text-gray-700">Aktif personel yok</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="card bg-gradient-to-br from-brand-500 to-indigo-600 border-0 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-4 h-4 opacity-90" />
                <p className="text-xs uppercase tracking-wide opacity-90">
                  {filtreVar
                    ? `Filtreli (${filtreliSatirlar.length} kişi) — Anlık Biriken`
                    : `${ayEtiketi(donem)} — Anlık Biriken`}
                </p>
              </div>
              <p className="text-3xl font-bold tabular-nums leading-tight">
                {filtreVar
                  ? (canliAkiyor ? paraKurus(filtreliAnlikToplam) : paraTam(filtreliAnlikToplam))
                  : (canliAkiyor ? paraKurus(yansitilanToplam) : paraTam(ozet.toplamAnlikMaliyet))}
                <span className="text-xl ml-1 opacity-80">₺</span>
              </p>
              <p className="text-[11px] opacity-75 mt-1">
                Çalışılan saat × saatlik karşılık
                {canliAkiyor && (
                  <span className="ml-2 inline-flex items-center gap-1">
                    • <Activity className="w-2.5 h-2.5 animate-pulse" /> canlı
                  </span>
                )}
              </p>
            </div>

            <div className="card bg-gradient-to-br from-emerald-500 to-teal-600 border-0 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <Calculator className="w-4 h-4 opacity-90" />
                <p className="text-xs uppercase tracking-wide opacity-90">
                  {filtreVar
                    ? `Filtreli (${filtreliSatirlar.length} kişi) — Ay Sonu Tahmini`
                    : `${ayEtiketi(donem)} — Ay Sonu Tahmini`}
                </p>
              </div>
              <p className="text-3xl font-bold tabular-nums leading-tight">
                {filtreVar ? paraTam(filtreliNetToplam) : paraTam(ozet.toplamTahminiNet)}
                <span className="text-xl ml-1 opacity-80">₺</span>
              </p>
              <p className="text-[11px] opacity-75 mt-1">
                Net ödeme (puantaj/avans/ek-kesinti dahil)
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 px-1">
              {filtreVar
                ? `Filtreli Personel (${filtreliSatirlar.length})`
                : 'Personel Detayı (Anlık Maliyete Göre)'}
            </p>
            {filtreliSatirlar.length === 0 ? (
              <div className="card text-center py-8 text-sm text-gray-500">
                Bu filtreye uyan personel yok.
              </div>
            ) : filtreliSatirlar.map(({ personel, hesap, saatlik, anlikMaliyet, tahminiNet }) => {
              const subeAdlari = personel.subeIdleri
                .map((id) => subeler.get(id)?.isim)
                .filter(Boolean) as string[]

              const aktifMi = aktifMaliyet?.detay.some((d) => d.personelId === personel.id) ?? false
              const yansitilanAnlik = yansitilanSatirlar.get(personel.id)
                ?? anlikMaliyet

              // YENİ — Aktif: yeşil, pasif: kırmızı renkli kart
              const kartStili = aktifMi
                ? 'ring-2 ring-green-400 bg-green-50/40'
                : 'ring-2 ring-red-500 bg-red-100'

              return (
                <div key={personel.id} className={`card ${kartStili}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-900 truncate">{personel.isim}</h3>
                        {aktifMi ? (
                          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full inline-flex items-center gap-0.5 font-semibold">
                            <Activity className="w-2.5 h-2.5 animate-pulse" />
                            Mesaide
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full inline-flex items-center gap-0.5 font-semibold">
                            <MoonStar className="w-2.5 h-2.5" />
                            Mesai Dışı
                          </span>
                        )}
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          {personel.maasTipi === 'aylik' ? 'Aylık' : 'Saatlik'}
                        </span>
                        {personel.calismaTipi === 'parttime' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                            Yarı
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {subeAdlari.join(', ')}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-600 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {hesap.toplamSaat.toFixed(1)} sa
                        </span>
                        <span>×</span>
                        <span>{paraTam(saatlik)} ₺/sa</span>
                        {hesap.puantajToplam !== 0 && (
                          <span className={hesap.puantajToplam > 0 ? 'text-green-700' : 'text-red-700'}>
                            P: {hesap.puantajToplam > 0 ? '+' : ''}{paraTam(hesap.puantajToplam)} ₺
                          </span>
                        )}
                        {hesap.odenmisAvansToplam > 0 && (
                          <span className="text-amber-700">
                            A: −{paraTam(hesap.odenmisAvansToplam)} ₺
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-2xl font-bold text-gray-900 tabular-nums">
                        {aktifMi && isBuAy
                          ? paraKurus(yansitilanAnlik)
                          : paraTam(yansitilanAnlik)}
                      </p>
                      <p className="text-xs text-gray-500">₺ anlık</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        ay sonu tahmini: {paraTam(tahminiNet)} ₺
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
